import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  api,
  type BillsView,
  type EngagementSummary,
  type Invoice,
} from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import {
  Button,
  Empty,
  Failed,
  Field,
  Label,
  Mark,
  SkeletonRows,
  TextArea,
} from "../components/primitives";
import { day, money, stamp } from "../lib/format";

/**
 * Bills — everything about money on this engagement.
 *
 * **Money is minor units everywhere.** Every number on this screen, in the
 * API and in the database is a whole number of kobo. The form multiplies
 * by 100 on the way in and `money()` divides by 100 on the way out, and
 * nothing in between ever holds a fraction. A float cannot represent 0.10
 * and a bill that is a penny out is a bill somebody has to explain.
 *
 * **Paid is derived.** The server sums payments against lines on every
 * read. There is no "paid" flag to fall out of step with the payments —
 * two sources of truth for that question means a day when they disagree
 * and somebody chases a client who has already paid.
 *
 * **A sent invoice is frozen and a void one is kept.** Lines stop being
 * editable the moment it goes out, and a bill raised in error is voided
 * with a reason rather than deleted: the number belongs to a series an
 * auditor will count, and a gap is what they ask about.
 *
 * **Who may bill** is `app.may_bill` — Super Admin or Principal Partner,
 * deliberately tighter than the `may_manage` that governs everything else
 * on an engagement. Billing a client is a commercial act of the firm, not
 * a step in delivering the work. A Project Lead sees the money on their
 * job and cannot raise a bill for it.
 */
export function Bills() {
  const { id } = useParams<{ id: string }>();
  const engagementId = id ?? "";

  const bills = useResource<BillsView>(
    () => api.get<BillsView>(`/api/engagements/${engagementId}/bills`),
    [engagementId],
  );
  const engagements = useResource<EngagementSummary[]>(
    () => api.get<EngagementSummary[]>("/api/engagements"),
    [],
  );

  const [raising, setRaising] = useState(false);

  const engagement =
    engagements.resource.state === "ready"
      ? engagements.resource.data.find((e) => e.id === engagementId)
      : undefined;

  const view = bills.resource.state === "ready" ? bills.resource.data : undefined;

  return (
    <AppShell
      engagement={
        engagement && {
          reference: engagement.reference,
          name: engagement.name,
          organisation: engagement.organisation,
        }
      }
    >
      <ScreenHead title="Bills" />

      {bills.resource.state === "loading" && <SkeletonRows rows={4} />}
      {bills.resource.state === "failed" && (
        <Failed
          message={bills.resource.message}
          correlationId={bills.resource.correlationId}
        />
      )}

      {view && (
        <>
          <Totals view={view} />

          {view.mayBill && (
            <div className="mt-6">
              {!raising ? (
                <Button tone="primary" onClick={() => setRaising(true)}>
                  Raise an invoice
                </Button>
              ) : (
                <RaiseInvoice
                  engagementId={engagementId}
                  currency={view.currency}
                  onDone={() => {
                    setRaising(false);
                    bills.reload();
                  }}
                  onCancel={() => setRaising(false)}
                />
              )}
            </div>
          )}

          {view.invoices.length === 0 && (
            <Empty title="Nothing billed yet">
              {view.mayBill
                ? "Raise one and it stays a draft until you send it."
                : "When the firm raises a bill on this engagement it appears here."}
            </Empty>
          )}

          {view.invoices.map((inv) => (
            <InvoiceRow key={inv.id} invoice={inv} onChanged={bills.reload} />
          ))}
        </>
      )}
    </AppShell>
  );
}

function Totals({ view }: { view: BillsView }) {
  const { totals, currency } = view;
  return (
    <section className="mt-8 border-b border-line pb-5">
      <dl className="flex flex-wrap gap-x-10 gap-y-3">
        <Figure label="Billed" value={money(totals.billed, currency)} />
        <Figure label="Paid" value={money(totals.paid, currency)} />
        <Figure
          label="Outstanding"
          value={money(totals.outstanding, currency)}
          tone={totals.outstanding > 0 ? "ink" : "secondary"}
        />
        {totals.overdue > 0 && (
          <Figure
            label="Overdue"
            value={money(totals.overdue, currency)}
            tone="danger"
          />
        )}
      </dl>
      <p className="mt-3 max-w-[62ch] text-xs text-muted">
        Sent invoices only. A draft is the firm&rsquo;s working document and is
        not money anybody owes.
      </p>
    </section>
  );
}

function Figure({
  label,
  value,
  tone = "ink",
}: {
  label: string;
  value: string;
  tone?: "ink" | "secondary" | "danger";
}) {
  const colour =
    tone === "danger" ? "text-danger" : tone === "secondary" ? "text-secondary" : "text-ink";
  return (
    <div>
      <dt className="label">{label}</dt>
      <dd data-numeric className={`mt-1 text-xl ${colour}`}>
        {value}
      </dd>
    </div>
  );
}

const STATUS_TONE: Record<string, "rest" | "live" | "warning" | "danger"> = {
  draft: "rest",
  sent: "warning",
  void: "rest",
};

function InvoiceRow({
  invoice: inv,
  onChanged,
}: {
  invoice: Invoice;
  onChanged: () => void;
}) {
  const [panel, setPanel] = useState<"pay" | "void" | null>(null);
  const [open, setOpen] = useState(false);

  const send = useAction(async () => {
    await api.post(`/api/invoices/${inv.id}/send`, {});
    onChanged();
  });

  const settled = inv.status === "sent" && inv.outstanding <= 0;

  return (
    <section className="mt-6 border-b border-line pb-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-sm text-ink">{inv.reference}</p>
          <p className="mt-1 text-sm text-secondary">
            {inv.issuedOn ? `Issued ${day(inv.issuedOn)}` : "Not issued"}
            {inv.dueOn && (
              <span className={inv.overdue ? "text-danger" : undefined}>
                {" · "}
                {inv.overdue ? "was due " : "due "}
                {day(inv.dueOn)}
              </span>
            )}
            {inv.createdBy && <span className="text-muted"> · {inv.createdBy}</span>}
          </p>
          {inv.status === "void" && inv.voidReason && (
            <p className="mt-1 max-w-[62ch] border-l-2 border-line pl-3 text-sm text-secondary">
              Void: {inv.voidReason}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="text-right">
            <p data-numeric className="text-lg text-ink">
              {money(inv.total, inv.currency)}
            </p>
            {inv.status === "sent" && (
              <p
                data-numeric
                className={`text-sm ${settled ? "text-secondary" : inv.overdue ? "text-danger" : "text-secondary"}`}
              >
                {settled
                  ? "settled"
                  : `${money(inv.outstanding, inv.currency)} outstanding`}
              </p>
            )}
          </div>
          <Mark tone={settled ? "live" : (STATUS_TONE[inv.status] ?? "rest")}>
            {settled ? "paid" : inv.status}
          </Mark>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="text-secondary underline-offset-4 hover:text-ink hover:underline"
        >
          {open ? "Hide" : "Show"} {inv.lines.length}{" "}
          {inv.lines.length === 1 ? "line" : "lines"}
          {inv.payments.length > 0 && ` and ${inv.payments.length} payment${inv.payments.length === 1 ? "" : "s"}`}
        </button>

        {inv.mayBill && inv.status === "draft" && (
          <Button
            tone="primary"
            loading={send.pending}
            onClick={() => void send.invoke()}
          >
            Send it
          </Button>
        )}
        {inv.mayBill && inv.status === "sent" && !settled && (
          <Button
            tone="secondary"
            onClick={() => setPanel((p) => (p === "pay" ? null : "pay"))}
          >
            Record a payment
          </Button>
        )}
        {inv.mayBill && inv.status !== "void" && (
          <Button
            tone="quiet"
            onClick={() => setPanel((p) => (p === "void" ? null : "void"))}
          >
            Void
          </Button>
        )}
      </div>

      {send.refusal && (
        <p className="mt-3 border-l-2 border-danger pl-4 text-sm text-ink">
          {send.refusal.message}
        </p>
      )}

      {open && (
        <div className="mt-4 border-l-2 border-line pl-4">
          <table className="w-full text-sm">
            <tbody>
              {inv.lines.map((l) => (
                <tr key={l.id} className="border-b border-line/60">
                  <td className="py-1.5 pr-4 text-ink">{l.description}</td>
                  <td data-numeric className="py-1.5 pr-4 text-right text-secondary">
                    {l.quantity === 100 ? "" : `${(l.quantity / 100).toFixed(2)} × `}
                    {money(l.unitAmount, inv.currency)}
                  </td>
                  <td data-numeric className="py-1.5 text-right text-ink">
                    {money(l.amount, inv.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {inv.payments.length > 0 && (
            <div className="mt-4">
              <Label as="h3">Payments</Label>
              <ul className="mt-1 text-sm">
                {inv.payments.map((p) => (
                  <li
                    key={p.id}
                    className="flex flex-wrap items-baseline justify-between gap-3 py-1 text-secondary"
                  >
                    <span>
                      {day(p.paidOn)}
                      {p.reference && <span className="text-muted"> · {p.reference}</span>}
                      {p.recordedBy && (
                        <span className="text-muted">
                          {" · recorded by "}
                          {p.recordedBy} {stamp(p.recordedAt)}
                        </span>
                      )}
                    </span>
                    <span data-numeric className="text-ink">
                      {money(p.amount, inv.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {inv.notes && (
            <p className="mt-4 max-w-[62ch] whitespace-pre-line text-sm text-secondary">
              {inv.notes}
            </p>
          )}
        </div>
      )}

      {panel === "pay" && (
        <RecordPayment
          invoice={inv}
          onDone={() => {
            setPanel(null);
            onChanged();
          }}
          onCancel={() => setPanel(null)}
        />
      )}
      {panel === "void" && (
        <VoidInvoice
          invoiceId={inv.id}
          onDone={() => {
            setPanel(null);
            onChanged();
          }}
          onCancel={() => setPanel(null)}
        />
      )}
    </section>
  );
}

/** Major units typed by a person → whole minor units for the wire. */
function toMinor(input: string): number | null {
  if (!/^-?\d+(\.\d{1,2})?$/.test(input.trim())) return null;
  // Parsed as a string and scaled by string manipulation rather than
  // `* 100`, because 19.99 * 100 is 1998.9999999999998 in binary floating
  // point and Math.round would hide that rather than avoid it.
  const [whole, frac = ""] = input.trim().split(".");
  const sign = whole!.startsWith("-") ? -1 : 1;
  const w = BigInt(whole!.replace("-", "") || "0");
  const f = BigInt((frac + "00").slice(0, 2));
  return sign * Number(w * 100n + f);
}

function RaiseInvoice({
  engagementId,
  currency,
  onDone,
  onCancel,
}: {
  engagementId: string;
  currency: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [dueOn, setDueOn] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ description: "", qty: "1", amount: "" }]);

  const parsed = lines.map((l) => ({
    description: l.description.trim(),
    qty: toMinor(l.qty),
    amount: toMinor(l.amount),
  }));
  const bad = parsed.some(
    (l) => !l.description || l.qty === null || l.qty <= 0 || l.amount === null,
  );
  const total = bad
    ? 0
    : parsed.reduce((n, l) => n + (l.qty! * l.amount!) / 100, 0);

  const raise = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/invoices`, {
      dueOn: dueOn || undefined,
      notes: notes.trim() || undefined,
      lines: parsed.map((l) => ({
        description: l.description,
        quantity: l.qty,
        unitAmount: l.amount,
      })),
    });
    onDone();
  });

  return (
    <div className="grid max-w-3xl gap-4 border-l-2 border-line pl-4">
      {lines.map((l, i) => (
        <div key={i} className="grid gap-3 sm:grid-cols-[1fr_6rem_9rem]">
          <Field
            label={i === 0 ? "Description" : ""}
            value={l.description}
            onChange={(e) =>
              setLines((ls) =>
                ls.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)),
              )
            }
          />
          <Field
            label={i === 0 ? "Quantity" : ""}
            inputMode="decimal"
            value={l.qty}
            onChange={(e) =>
              setLines((ls) => ls.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))
            }
          />
          <Field
            label={i === 0 ? `Unit (${currency})` : ""}
            inputMode="decimal"
            hint={i === 0 ? "A negative amount is a discount." : undefined}
            value={l.amount}
            onChange={(e) =>
              setLines((ls) =>
                ls.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)),
              )
            }
          />
        </div>
      ))}

      <div>
        <Button
          tone="quiet"
          onClick={() =>
            setLines((ls) => [...ls, { description: "", qty: "1", amount: "" }])
          }
        >
          Another line
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Due"
          type="date"
          hint="Optional."
          value={dueOn}
          onChange={(e) => setDueOn(e.target.value)}
        />
        <div className="flex items-end">
          <p className="text-sm text-secondary">
            Total{" "}
            <span data-numeric className="text-base text-ink">
              {bad ? "—" : money(total, currency)}
            </span>
          </p>
        </div>
      </div>

      <TextArea
        label="Notes"
        hint="Optional. Appears on the invoice the client sees."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      <div className="flex gap-3">
        <Button
          tone="primary"
          loading={raise.pending}
          disabled={bad}
          onClick={() => void raise.invoke()}
        >
          Save as draft
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <p className="max-w-[62ch] text-xs text-muted">
        It stays a draft, invisible to the client, until you send it. Once
        sent its lines are fixed — a correction is a credit line on a new
        invoice, or a void with a reason.
      </p>

      {raise.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink">
          {raise.refusal.message}
        </p>
      )}
    </div>
  );
}

function RecordPayment({
  invoice: inv,
  onDone,
  onCancel,
}: {
  invoice: Invoice;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState((inv.outstanding / 100).toFixed(2));
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");

  const minor = toMinor(amount);

  const record = useAction(async () => {
    await api.post(`/api/invoices/${inv.id}/payments`, {
      amount: minor,
      paidOn,
      reference: reference.trim() || undefined,
    });
    onDone();
  });

  return (
    <div className="mt-4 grid max-w-2xl gap-4 border-l-2 border-line pl-4 sm:grid-cols-3">
      <Field
        label={`Amount (${inv.currency})`}
        inputMode="decimal"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        error={minor === null || minor <= 0 ? "A positive amount." : undefined}
      />
      <Field
        label="Paid on"
        type="date"
        value={paidOn}
        onChange={(e) => setPaidOn(e.target.value)}
      />
      <Field
        label="Their reference"
        hint="So it reconciles against a statement."
        value={reference}
        onChange={(e) => setReference(e.target.value)}
      />
      <div className="flex gap-3 sm:col-span-3">
        <Button
          tone="primary"
          loading={record.pending}
          disabled={minor === null || minor <= 0 || !paidOn}
          onClick={() => void record.invoke()}
        >
          Record it
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {record.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink sm:col-span-3">
          {record.refusal.message}
        </p>
      )}
    </div>
  );
}

function VoidInvoice({
  invoiceId,
  onDone,
  onCancel,
}: {
  invoiceId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");

  const act = useAction(async () => {
    await api.post(`/api/invoices/${invoiceId}/void`, { reason: reason.trim() });
    onDone();
  });

  return (
    <div className="mt-4 grid max-w-2xl gap-3 border-l-2 border-line pl-4">
      <TextArea
        label="Why it is void"
        hint="Required. The invoice is kept — its number belongs to a series, and a gap is what an auditor asks about."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex gap-3">
        <Button
          tone="danger"
          loading={act.pending}
          disabled={!reason.trim()}
          onClick={() => void act.invoke()}
        >
          Void it
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {act.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink">
          {act.refusal.message}
        </p>
      )}
    </div>
  );
}
